import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Container,
  Divider,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material'
import useApprovalRowDetails, {
  ApprovalRowDetail,
} from '@/hooks/useApprovalRowDetails'
import useOverlay from '@/hooks/useOverlay'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import useUsersById from '@/hooks/useUsersById'
import useValidationNavList from '@/hooks/useValidationNavList'
import {
  useGetApprovalHistory,
  useGetWaitingApprovals,
} from '@/service-library/hooks/my-approvals'
import { ApprovalHistoryEntry, WaitingApproval } from '@/types/approvals'
import {
  ApprovalScreen,
  ApprovalTableRow,
  availableColumns,
} from '@/utils/approval-columns'
import { maxReviewersWidth } from '@/utils/my-approvals'
import { getIdleTime } from '@/utils/time'
import PageTitle from '@/components/PageTitle'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import ValidationOverlay from '@/components/validation-overlay/ValidationOverlay'
import ApprovalTable from './ApprovalTable'

const NO_ITEMS: never[] = []

function approverIdsOf(
  entries: (WaitingApproval | ApprovalHistoryEntry)[],
): string[] {
  return entries.flatMap((entry) =>
    (entry.policy?.approvers ?? []).flatMap((approver) => {
      if (approver.kind !== 'user') return []
      const actedBy = approver.latest_action?.acted_by_user_id
      return actedBy ? [approver.user_id, actedBy] : [approver.user_id]
    }),
  )
}

function documentIdsOf(
  entries: (WaitingApproval | ApprovalHistoryEntry)[],
): string[] {
  return entries.map((entry) => entry.document_id)
}

function uniqueDocumentIdsOf(
  entries: (WaitingApproval | ApprovalHistoryEntry)[],
): string[] {
  return [...new Set(documentIdsOf(entries))]
}

function waitingRows(
  entries: WaitingApproval[],
  details: Map<string, ApprovalRowDetail>,
): ApprovalTableRow[] {
  return entries.map((entry) => ({
    // A document blocking on two policies is two rows, so neither id alone is unique.
    id: `${entry.document_id}:${entry.policy.approval_policy_id}`,
    documentId: entry.document_id,
    policy: entry.policy,
    time: entry.waiting_since ? getIdleTime(entry.waiting_since) : 'Unknown',
    detail: details.get(entry.document_id),
  }))
}

function historyRows(
  entries: ApprovalHistoryEntry[],
  details: Map<string, ApprovalRowDetail>,
): ApprovalTableRow[] {
  return entries.map((entry) => ({
    // One action per row: approving the same document twice is two entries.
    id: entry.action.id,
    documentId: entry.document_id,
    policy: entry.policy,
    time: `${getIdleTime(entry.action.created_at, false, false)} ago`,
    detail: details.get(entry.document_id),
    action: entry.action,
  }))
}

export default function MyApprovalsPage() {
  const { project } = useProjectContext()

  const documentIdState = useStatefulSearchParam({ param: 'document_id' })
  const validationOverlay = useOverlay()

  useEffect(() => {
    if (!documentIdState.isInUrl && validationOverlay.isOpen) {
      validationOverlay.close()
    }
  }, [documentIdState.isInUrl, validationOverlay])

  function openDocument(documentId: string) {
    documentIdState.toggleValue(documentId, false)
    validationOverlay.open()
  }

  const [tab, setTab] = useState<'waiting' | 'history'>('waiting')
  const showWaiting = tab === 'waiting'
  const showHistory = !showWaiting

  const waiting = useGetWaitingApprovals({
    projectId: project.id,
    enabled: showWaiting,
  })
  const history = useGetApprovalHistory({
    projectId: project.id,
    enabled: showHistory,
  })

  // A hidden tab's query is disabled, but React Query keeps the rows it
  // already had. Treating them as empty here keeps the row-details and user
  // lookups below from asking about a tab nobody can see.
  const waitingItems: WaitingApproval[] = showWaiting
    ? waiting.waitingApprovals
    : NO_ITEMS
  const historyItems: ApprovalHistoryEntry[] = showHistory
    ? history.approvalHistory
    : NO_ITEMS

  // Deduped so prev/next in the overlay doesn't stop twice on a document that
  // is blocking on two policies.
  const sections = useMemo(
    () => [
      { query: waiting, documentIds: uniqueDocumentIdsOf(waitingItems) },
      { query: history, documentIds: uniqueDocumentIdsOf(historyItems) },
    ],
    [waiting, waitingItems, history, historyItems],
  )
  const openDocumentId = documentIdState.value
  const activeSection = useMemo(
    () =>
      openDocumentId
        ? sections.find(({ documentIds }) =>
            documentIds.includes(openDocumentId),
          )
        : undefined,
    [sections, openDocumentId],
  )
  const navDocumentIds = activeSection?.documentIds ?? []

  // TanStack Query's `refetch` ignores `enabled`. Without these checks, closing
  // the overlay would also fetch the hidden tab.
  const refetchLists = useCallback(() => {
    if (showWaiting) waiting.refetch()
    if (showHistory) history.refetch()
  }, [waiting, showWaiting, history, showHistory])

  const closeDocument = useCallback(() => {
    validationOverlay.close()
    documentIdState.resetValue(false)
    refetchLists()
  }, [validationOverlay, documentIdState, refetchLists])

  const navList = useValidationNavList({
    currentDocumentId: openDocumentId,
    documentIds: navDocumentIds,
    setDocumentId: documentIdState.toggleValue,
    rowsCount: navDocumentIds.length,
    fetchNextPage:
      activeSection?.query.hasNextPage &&
      !activeSection.query.isFetchingNextPage &&
      validationOverlay.isOpen
        ? activeSection.query.fetchNextPage
        : undefined,
    onClose: closeDocument,
  })

  const waitingDetails = useApprovalRowDetails(documentIdsOf(waitingItems))
  const historyDetails = useApprovalRowDetails(documentIdsOf(historyItems))

  // Only one call is read: which columns exist comes from the project's fields,
  // so both tabs get the same answer.
  const { availability } = waitingDetails

  const hasRoomForAll = useMediaQuery((theme) => theme.breakpoints.up('md'))
  const hasSomeRoom = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const screen: ApprovalScreen = hasRoomForAll
    ? 'md'
    : hasSomeRoom
    ? 'sm'
    : 'xs'

  const waitingColumns = useMemo(
    () =>
      availableColumns(
        availability,
        'Waiting',
        maxReviewersWidth(waitingItems.map(({ policy }) => policy)),
      ),
    [availability, waitingItems],
  )
  const historyColumns = useMemo(
    () =>
      availableColumns(
        availability,
        'Decided',
        maxReviewersWidth(historyItems.map(({ policy }) => policy)),
        true,
      ),
    [availability, historyItems],
  )

  const approverIds = useMemo(
    () => approverIdsOf([...waitingItems, ...historyItems]),
    [waitingItems, historyItems],
  )
  const { usersById, isPending: usersPending } = useUsersById({
    userIds: approverIds,
    enabled: approverIds.length > 0,
  })

  const sharedTableProps = {
    screen,
    usersById,
    usersPending,
    onOpenDocument: openDocument,
  }

  const waitingTableRows = useMemo(
    () => waitingRows(waitingItems, waitingDetails.detailsByDocumentId),
    [waitingItems, waitingDetails.detailsByDocumentId],
  )
  const historyTableRows = useMemo(
    () => historyRows(historyItems, historyDetails.detailsByDocumentId),
    [historyItems, historyDetails.detailsByDocumentId],
  )

  return (
    <Container maxWidth="lg" sx={{ py: 3.5 }}>
      <PageTitle>Approvals</PageTitle>
      <Typography variant="h5" fontWeight={800}>
        Approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Documents assigned to you for review and sign-off.
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, value: 'waiting' | 'history') => setTab(value)}
        >
          <Tab label="Waiting" value="waiting" />
          <Tab label="History" value="history" />
        </Tabs>
        <Divider />
      </Box>

      <Box sx={{ mt: 2.5 }}>
        {showWaiting ? (
          <ApprovalTable
            key="waiting"
            columns={waitingColumns}
            hasMore={!!waiting.hasNextPage && !waiting.isFilling}
            isLoading={
              waiting.isLoading ||
              (waitingItems.length === 0 && waiting.isFilling)
            }
            isFetchingMore={waiting.isFetchingNextPage}
            onLoadMore={waiting.fetchNextPage}
            emptyMessage="Nothing is waiting on your approval in this project."
            rows={waitingTableRows}
            {...sharedTableProps}
          />
        ) : (
          <ApprovalTable
            key="history"
            columns={historyColumns}
            hasMore={!!history.hasNextPage}
            isLoading={history.isLoading}
            isFetchingMore={history.isFetchingNextPage}
            onLoadMore={history.fetchNextPage}
            emptyMessage="You haven't approved or rejected anything in this project yet."
            rows={historyTableRows}
            {...sharedTableProps}
          />
        )}
      </Box>
      <Box sx={{ height: 24 }} />

      <ValidationOverlay
        overlay={validationOverlay}
        documentIdState={documentIdState}
        documentsCount={navDocumentIds.length}
        refetchTableData={refetchLists}
        disableStateRedirect
        preferredView="approvals"
        {...navList}
      />
    </Container>
  )
}
